<template>
  <div class="relative bg-koth-bg bg-fixed bg-cover bg-center bg-[#00284F] pt-[80px]">
    <!-- Content -->
    <div
      class="relative z-10 flex flex-col items-center text-white text-center py-12 px-4 sm:px-6"
    >
      <!-- Title -->
      <h1
        v-motion-pop-visible
        class="text-5xl md:text-[70px] font-black uppercase font-golos"
      >
        King of the Hill
      </h1>

      <!-- Countdown Timer -->
      <div class="flex justify-center gap-2 sm:gap-4 mt-10">
        <!-- Loading Spinner -->
        <div v-if="loading" class="flex justify-center gap-2 sm:gap-4 text-black">
          <div
            class="bg-[#1D1D1D] bg-opacity-50 border border-[#363636] rounded-md px-4 py-4 text-white flex justify-center items-center"
          >
            <span
              class="animate-spin border-2 border-white border-t-transparent rounded-full w-7 sm:w-12 h-7 sm:h-12"
            ></span>
          </div>
          <div
            class="bg-[#1D1D1D] bg-opacity-50 border border-[#363636] rounded-md px-4 py-4 text-white flex justify-center items-center"
          >
            <span
              class="animate-spin border-2 border-white border-t-transparent rounded-full w-7 sm:w-12 h-7 sm:h-12"
            ></span>
          </div>
          <div
            class="bg-[#1D1D1D] bg-opacity-50 border border-[#363636] rounded-md px-4 py-4 text-white flex justify-center items-center"
          >
            <span
              class="animate-spin border-2 border-white border-t-transparent rounded-full w-7 sm:w-12 h-7 sm:h-12"
            ></span>
          </div>
          <div
            class="bg-[#1D1D1D] bg-opacity-50 border border-[#363636] rounded-md px-4 py-4 text-white flex justify-center items-center"
          >
            <span
              class="animate-spin border-2 border-white border-t-transparent rounded-full w-7 sm:w-12 h-7 sm:h-12"
            ></span>
          </div>
        </div>

        <!-- Countdown Values -->
        <div v-else class="flex flex-wrap justify-center gap-2">
          <span
            v-motion-pop-visible
            class="bg-[#1D1D1D] bg-opacity-50 border border-[#363636] px-4 py-4 sm:px-6 sm:py-6 rounded text-lg sm:text-2xl font-golos font-black"
            >{{ days }}d</span
          >
          <span
            v-motion-pop-visible
            class="bg-[#1D1D1D] bg-opacity-50 border border-[#363636] px-4 py-4 sm:px-6 sm:py-6 rounded text-lg sm:text-2xl font-golos font-black"
            >{{ hours }}h</span
          >
          <span
            v-motion-pop-visible
            class="bg-[#1D1D1D] bg-opacity-50 border border-[#363636] px-4 py-4 sm:px-6 sm:py-6 rounded text-lg sm:text-2xl font-golos font-black"
            >{{ minutes }}m</span
          >
          <span
            v-motion-pop-visible
            class="bg-[#1D1D1D] bg-opacity-50 border border-[#363636] px-4 py-4 sm:px-6 sm:py-6 rounded text-lg sm:text-2xl font-golos font-black"
            >{{ seconds }}s</span
          >
        </div>
      </div>

      <!-- Info Box -->
      <div
        v-motion-pop-visible
        class="mt-6 bg-[#1D1D1D] bg-opacity-50 border border-[#363636] p-4 sm:p-6 rounded-lg w-full max-w-4xl"
      >
        <p class="text-sm sm:text-base text-[#A3B3C2] font-montserrat">
          All challenges are counted during the inclusive dates:
          <span
            v-if="activeTab == 'current'"
            class="text-[#1294F0] font-bold block mt-4 sm:inline sm:mt-0"
            >{{ startDate }} - {{ endDate }}</span
          >
          <span v-else class="text-[#1294F0] font-bold block mt-4 sm:inline sm:mt-0"
            >{{ prevStartDate }} - {{ prevEndDate }}</span
          >
        </p>
      </div>

      <!-- Info Box 2 -->
      <div
        v-motion-pop-visible
        class="mt-6 bg-[#1D1D1D] bg-opacity-50 p-4 sm:p-6 rounded-lg w-full max-w-4xl border border-[#363636] shadow-lg"
      >
        <div class="flex justify-center items-center gap-2 text-white font-bold text-lg">
          <img src="@/assets/info-icon.png" alt="info" class="w-5 h-5" />
          How does KOTH work?
        </div>
        <p class="text-sm text-[#A3B3C2] mt-2 font-montserrat">
          Place a bet on Metaspins or Dailyspins with a minimum bet of
          <span class="font-bold">0.40</span> for slots and hold the throne for the
          entire month to claim your rewards.
        </p>
      </div>

      <!-- Current and Previous Winners Buttons -->
      <div class="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-[42rem]">
        <!-- Current Winners Button -->
        <button
          v-motion-pop-visible
          @click="changeTab('current')"
          :class="[
            'px-4 py-4 sm:px-6 sm:py-3 rounded-lg font-bold flex items-center gap-2 justify-center w-full transition-all duration-300',
            activeTab === 'current'
              ? 'bg-[#1294F0] text-white'
              : 'bg-[#1D1D1D] text-[#A3B3C2]',
          ]"
        >
          <img
            src="@/assets/crown-icon.svg"
            class="w-5 h-5"
            :style="
              activeTab === 'current'
                ? 'filter: brightness(0) saturate(100%) invert(86%) sepia(85%) saturate(564%) hue-rotate(2deg) brightness(100%) contrast(95%)'
                : 'filter: brightness(0) saturate(100%) invert(66%) sepia(4%) saturate(220%) hue-rotate(176deg) brightness(95%) contrast(92%)'
            "
          />
          Current Winners
        </button>

        <!-- Previous Winners Button -->
        <button
          v-motion-pop-visible
          @click="changeTab('previous')"
          :class="[
            'px-4 py-4 sm:px-6 sm:py-3 rounded-lg font-bold flex items-center gap-2 justify-center w-full transition-all duration-300',
            activeTab === 'previous'
              ? 'bg-[#1294F0] text-white'
              : 'bg-[#1D1D1D] text-[#A3B3C2]',
          ]"
        >
          <img
            src="@/assets/crown-icon.svg"
            class="w-5 h-5"
            :style="
              activeTab === 'previous'
                ? 'filter: brightness(0) saturate(100%) invert(86%) sepia(85%) saturate(564%) hue-rotate(2deg) brightness(100%) contrast(95%)'
                : 'filter: brightness(0) saturate(100%) invert(66%) sepia(4%) saturate(220%) hue-rotate(176deg) brightness(95%) contrast(92%)'
            "
          />
          Previous Winners
        </button>
      </div>

      <!-- Winners Section -->
      <div class="mt-10 flex flex-col w-full sm:w-auto lg:flex-row gap-4 sm:gap-6">
        <!-- Highest Slots Winner -->
        <div
          v-motion-pop-visible
          class="bg-gradient-to-r from-[#1D1D1D] to-[#313B44] p-6 rounded-xl w-full sm:w-80 min-h-[400px] text-center border border-[#363636] shadow-lg"
        >
          <!-- Spinner for Highest Slots Winner Card -->
          <div v-if="loading" class="flex justify-center items-center w-full h-full">
            <div
              class="bg-transparent rounded-md px-4 py-4 text-white flex justify-center items-center"
            >
              <span
                class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
              ></span>
            </div>
          </div>

          <!-- Content for Highest Slots Winner -->
          <div v-else>
            <div
              class="flex items-center justify-center gap-2 bg-[#FFD166] bg-opacity-20 border border-[#FFB200]/50 text-[#FFD166] px-4 py-1 rounded-full text-sm font-montserrat font-bold w-fit mx-auto"
            >
              <img src="@/assets/trophy-icon.png" class="w-4 h-4" />
              Highest Any Slots
            </div>
            <div class="mt-4">
              <img
                :src="
                  slotData.userDetails?.avatar
                    ? `https://cdn.discordapp.com/avatars/${slotData.userDetails.discordId}/${slotData.userDetails.avatar}.png`
                    : require('@/assets/avatar-1.png')
                "
                class="w-16 h-16 mx-auto rounded-full border-2 border-yellow-400"
              />
              <h3 class="mt-2 text-sm text-[#A3B3C2] font-montserrat">
                {{ slotData.username || "—" }}
              </h3>
            </div>
            <p class="mt-6 font-extrabold text-base">KOTH SLOTS</p>

            <div class="mt-6 text-sm text-[#A3B3C2] flex flex-col gap-1">
              <div class="flex justify-between font-montserrat">
                <span>Bet ID:</span>
                <a
                  :href="slotData.betId || '#'"
                  target="_blank"
                  class="text-[#1294F0] font-bold"
                >
                  View Bet
                </a>
              </div>
              <div class="flex justify-between font-montserrat">
                <span>Multiplier:</span>
                <span class="text-white">x{{ slotData.multi || "N/A" }}</span>
              </div>
              <div class="flex justify-between font-montserrat text-[#A3B3C2]">
                <span>Reward:</span>
                <span class="text-[#EEAF0E] font-orbitron">
                  $
                  {{ activeTab == "current" ? slotCurrentReward[0] : slotPrevReward[0] }}
                </span>
              </div>

              <!-- View Rules Button -->
              <button
                @click="openRulesModal('slot')"
                class="mt-4 px-6 py-4 bg-gradient-to-r from-[#1D1D1D] to-[#313B44] text-white rounded-lg text-sm font-bold hover:bg-[#E87A0E] border-2 border-transparent transition duration-300 hover:border-white"
              >
                View Rules
              </button>
            </div>
          </div>
        </div>

        <!-- <div
          v-motion-pop-visible
          class="bg-gradient-to-r from-[#1D1D1D] to-[#313B44] p-6 rounded-xl w-full sm:w-80 min-h-[400px] text-center border border-[#363636] shadow-lg"
        >
          <div v-if="loading" class="flex justify-center items-center w-full h-full">
            <div
              class="bg-transparent rounded-md px-4 py-4 text-white flex justify-center items-center"
            >
              <span
                class="animate-spin border-2 border-white border-t-transparent rounded-full w-12 h-12"
              ></span>
            </div>
          </div>

          <div v-else>
            <div
              class="flex items-center justify-center gap-2 bg-[#FFD166] bg-opacity-20 border border-[#FFB200]/50 text-[#FFD166] px-4 py-1 rounded-full text-sm font-montserrat font-bold w-fit mx-auto"
            >
              <img src="@/assets/trophy-icon.png" class="w-4 h-4" />
              Highest Sports
            </div>
            <div class="mt-4">
              <img
                :src="
                  sportData.userDetails?.avatar
                    ? `https://cdn.discordapp.com/avatars/${sportData.userDetails.discordId}/${sportData.userDetails.avatar}.png`
                    : require('@/assets/avatar-2.png')
                "
                class="w-16 h-16 mx-auto rounded-full border-2 border-yellow-400"
              />
              <h3 class="mt-2 text-sm text-[#A3B3C2] font-montserrat">
                {{ sportData.username || "—" }}
              </h3>
            </div>
            <p class="mt-6 font-extrabold text-base">KOTH SPORTS BET</p>

            <div class="mt-6 text-sm text-[#A3B3C2] flex flex-col gap-1">
              <div class="flex justify-between font-montserrat">
                <span>Bet ID:</span>
                <a
                  :href="sportData.betId || '#'"
                  target="_blank"
                  class="text-[#1294F0] font-bold"
                >
                  View Bet
                </a>
              </div>
              <div class="flex justify-between font-montserrat">
                <span>Payout:</span>
                <span class="text-white">${{ sportData.multi || "N/A" }}</span>
              </div>
              <div class="flex justify-between font-montserrat text-[#A3B3C2]">
                <span>Reward:</span>
                <span class="text-[#EEAF0E] font-orbitron">
                  $
                  {{
                    activeTab == "current" ? sportCurrentReward[0] : sportPrevReward[0]
                  }}
                </span>
              </div>

              <button
                @click="openRulesModal('sport')"
                class="mt-4 px-6 py-4 bg-gradient-to-r from-[#1D1D1D] to-[#313B44] text-white rounded-lg text-sm font-bold hover:bg-[#E87A0E] border-2 border-transparent transition duration-300 hover:border-white"
              >
                View Rules
              </button>
            </div>
          </div>
        </div> -->
      </div>
    </div>
  </div>

  <!-- KOTH Rules Modal -->
  <KothRulesModal
    :isVisible="isModalVisible"
    :slotRules="showSlotRules"
    :sportRules="showSportRules"
    @close="closeModal"
  />
</template>

<script>
import axios from "axios";
import moment from "moment-timezone";
import { defineComponent } from "vue";
import KothRulesModal from "../components/KothRulesModal.vue";

export default defineComponent({
  components: {
    KothRulesModal,
  },
  data() {
    return {
      activeTab: "current",

      days: "",
      hours: "",
      minutes: "",
      seconds: "",

      slotData: [],
      sportData: [],

      slotCurrentData: [],
      slotPrevData: [],
      sportsCurrentData: [],
      sportsPrevData: [],

      startDate: null,
      endDate: null,

      prevStartDate: null,
      prevEndDate: null,

      notFormattedEndDate: null,
      loading: false,

      slotCurrentReward: JSON.parse(process.env.VUE_APP_SLOT_LEADERBOARD_CURRENT_PRIZES),
      slotPrevReward: JSON.parse(process.env.VUE_APP_SLOT_LEADERBOARD_PREV_PRIZES),
      sportCurrentReward: JSON.parse(
        process.env.VUE_APP_SPORTBET_LEADERBOARD_CURRENT_PRIZES
      ),
      sportPrevReward: JSON.parse(process.env.VUE_APP_SPORTBET_LEADERBOARD_PREV_PRIZES),

      slotRules: false,
      sportRules: false,

      isModalVisible: false,
      showSlotRules: false,
      showSportRules: false,
    };
  },
  methods: {
    async init() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      this.loading = true;

      await axios
        .get(`${baseUrl}/api/leaderboard/slot-challenges`, {
          headers: { "x-api-key": process.env.VUE_APP_X_API_KEY },
        })
        .then((res) => {
          this.slotCurrentData =
            res.data.leaderboardData.length > 0 ? res.data.leaderboardData[0] : [];

          this.startDate = moment(res.data.startDate).utc().format("MMMM DD, YYYY");
          this.endDate = moment(res.data.endDate).utc().format("MMMM DD, YYYY");

          this.notFormattedEndDate = res.data.endDate;
        });

      await axios
        .get(`${baseUrl}/api/leaderboard/slot-challenges?previous=true`, {
          headers: { "x-api-key": process.env.VUE_APP_X_API_KEY },
        })
        .then((res) => {
          this.slotPrevData =
            res.data.leaderboardData.length > 0 ? res.data.leaderboardData[0] : [];

          this.prevStartDate = moment(res.data.startDate).utc().format("MMMM DD, YYYY");
          this.prevEndDate = moment(res.data.endDate).utc().format("MMMM DD, YYYY");
        });

      // await axios
      //   .get(`${baseUrl}/api/leaderboard/koth/sportsbet`, {
      //     headers: { "x-api-key": process.env.VUE_APP_X_API_KEY },
      //   })
      //   .then((res) => {
      //     this.sportsCurrentData =
      //       res.data.leaderboardData.length > 0 ? res.data.leaderboardData[0] : [];
      //   });

      // await axios
      //   .get(`${baseUrl}/api/leaderboard/koth/sportsbet?previous=true`, {
      //     headers: { "x-api-key": process.env.VUE_APP_X_API_KEY },
      //   })
      //   .then((res) => {
      //     this.sportsPrevData =
      //       res.data.leaderboardData.length > 0 ? res.data.leaderboardData[0] : [];
      //   });

      this.changeLeaderboard();
      this.startTimer();

      this.loading = false;
    },
    changeLeaderboard() {
      if (this.activeTab == "current") {
        this.slotData = this.slotCurrentData;
        this.sportData = this.sportsCurrentData;
      } else {
        this.slotData = this.slotPrevData;
        this.sportData = this.sportsPrevData;
      }
    },
    getOrdinalSuffix(n) {
      const s = ["th", "st", "nd", "rd"],
        v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    maskString(str) {
      if (!str || str.length < 2) {
        return str;
      }
      if (str.length <= 4) {
        const masked = "*".repeat(str.length - 1);
        const last = str.slice(-1);
        return `${masked}${last}`;
      } else {
        const masked = "*".repeat(4);
        const rest = str.slice(4);
        return `${masked}${rest}`;
      }
    },
    getRewardValue(rank) {
      const prizes = JSON.parse(process.env.VUE_APP_SLOT_LEADERBOARD_CURRENT_PRIZES);
      if (rank >= prizes.length) {
        return "X";
      } else {
        return "$ " + prizes[rank];
      }
    },
    getPrevRewardValue(rank) {
      const prizes = JSON.parse(process.env.VUE_APP_SLOT_LEADERBOARD_PREV_PRIZES);
      if (rank >= prizes.length) {
        return "X";
      } else {
        return "$ " + prizes[rank];
      }
    },
    startTimer() {
      const getNextTargetDate = () => {
        // const now = moment.utc();

        let targetDate = moment.utc(this.notFormattedEndDate).subtract(1, "seconds");

        // if (now.isAfter(targetDate)) {
        //   targetDate.add(1, 'week');
        // }

        return targetDate;
      };

      let targetDate = getNextTargetDate();
      let timerInterval;

      const updateTimer = () => {
        const nowUtc = moment.utc();
        const distance = targetDate.diff(nowUtc);

        if (distance < 0) {
          // this.init();
          clearInterval(timerInterval);

          // targetDate = getNextTargetDate();
          // this.startTimer();

          this.days = 0;
          this.hours = 0;
          this.minutes = 0;
          this.seconds = 0;
        } else {
          const duration = moment.duration(distance);
          this.days = Math.floor(duration.asDays());
          this.hours = duration.hours();
          this.minutes = duration.minutes();
          this.seconds = duration.seconds();
        }
      };

      updateTimer();
      timerInterval = setInterval(updateTimer, 1000);
    },
    changeTab(tab) {
      this.activeTab = tab;
      this.changeLeaderboard();
    },
    openRulesModal(type) {
      this.isModalVisible = true;
      if (type === "slot") {
        this.showSlotRules = true;
        this.showSportRules = false;
      } else if (type === "sport") {
        this.showSportRules = true;
        this.showSlotRules = false;
      }
    },
    closeModal() {
      this.isModalVisible = false;
      this.showSlotRules = false;
      this.showSportRules = false;
    },
    isMobile() {
      return window.innerWidth < 1024;
    },
  },
  mounted() {
    this.init();
  },
});
</script>